import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { Layout, Menu, Typography, Avatar, Button, Tooltip } from "antd";
import {
  MdDashboard,
  MdSettings,
  MdLogout,
  MdMenu,
  MdMenuOpen,
  MdPerson,
  MdDescription,
  MdBarChart,
  MdShoppingCart,
  MdImportExport,
  MdCheckCircle,
  MdPauseCircle,
  MdTableChart,
  MdAssessment,
  MdReceipt,
  MdNotifications,
  MdPeople,
  MdBuild,
  MdQueue,
  MdAssignment,
  MdLocalShipping,
  MdFactCheck,
  MdTableRows,
  MdFormatTextdirectionRToL,
} from "react-icons/md";
import { useMaterialUIController, setMiniSidenav } from "context";
import { logout } from "../../actions/authentication/auth";
import { toast } from "react-toastify";

const { Sider } = Layout;
const { Text } = Typography;

// تبدیل آیکون‌های Material-UI به react-icons
const getReactIcon = (icon) => {
  if (!icon) return <MdDashboard />;
  
  // اگر React element است
  if (typeof icon === "object" && icon !== null && icon.type) {
    // اگر icon یک component Material-UI Icon است (مثل <Icon>dashboard</Icon>)
    const isMaterialIcon = 
      icon.type?.displayName === "Icon" || 
      icon.type?.name === "Icon" ||
      (typeof icon.type === "function" && icon.type.toString().includes("Icon"));
    
    if (isMaterialIcon) {
      const iconText = icon.props?.children || "";
      const iconMapString = {
        "dashboard": MdDashboard,
        "description": MdDescription,
        "assignment": MdAssignment,
        "fact_check": MdFactCheck,
        "pause_circle": MdPauseCircle,
        "table_view": MdTableChart,
        "receipt_long": MdReceipt,
        "format_textdirection_r_to_l": MdFormatTextdirectionRToL,
        "notifications": MdNotifications,
        "person": MdPerson,
        "exit_to_app": MdLogout,
      };
      const IconComponent = iconMapString[iconText] || MdDashboard;
      return <IconComponent style={{ fontSize: 18 }} />;
    }
    
    // اگر icon یک component Material-UI Icon است (مثل DescriptionIcon, QueueRoundedIcon)
    const componentName = icon.type.displayName || icon.type.name || "";
    const iconMap = {
      "DescriptionIcon": MdDescription,
      "AssessmentIcon": MdAssessment,
      "QueueRoundedIcon": MdQueue,
      "ImportExportRounded": MdImportExport,
      "LocalShippingIcon": MdLocalShipping,
      "AssessmentRounded": MdAssessment,
      "BarChartRoundedIcon": MdBarChart,
    };
    
    if (iconMap[componentName]) {
      const IconComponent = iconMap[componentName];
      return <IconComponent style={{ fontSize: 18 }} />;
    }
    
    // اگر icon خودش یک React element است، آن را در یک wrapper قرار بده
    return <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 18 }}>{icon}</span>;
  }

  return <MdDashboard style={{ fontSize: 18 }} />;
};

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(miniSidenav);
  const [selectedKey, setSelectedKey] = useState("");

  useEffect(() => {
    const path = location.pathname;
    const currentRoute = routes.find((r) => r.route === path || path.startsWith(r.route + "/"));
    if (currentRoute) {
      setSelectedKey(currentRoute.key || "");
    } else {
      setSelectedKey(path.replace("/", "") || "");
    }
  }, [location.pathname, routes]);

  useEffect(() => {
    setCollapsed(miniSidenav);
  }, [miniSidenav]);

  // Inject custom styles
  useEffect(() => {
    const styleId = "custom-sidenav-styles";
    if (document.getElementById(styleId)) {
      return;
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      .custom-sider {
        scrollbar-width: thin;
        scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
      }
      .custom-sider::-webkit-scrollbar {
        width: 6px;
      }
      .custom-sider::-webkit-scrollbar-track {
        background: transparent;
      }
      .custom-sider::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 3px;
      }
      .custom-sider::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.3);
      }
      .custom-sider .ant-layout-sider-trigger {
        background: rgba(0, 0, 0, 0.3);
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        color: #fff;
      }
      .custom-sider .ant-layout-sider-trigger:hover {
        background: rgba(0, 0, 0, 0.5);
      }
      .custom-menu .ant-menu-item {
        margin: 4px 8px;
        border-radius: 8px;
        height: 48px;
        line-height: 48px;
        font-size: 14px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: 'Vazir', sans-serif;
      }
      .custom-menu .ant-menu-item:hover {
        background: rgba(255, 255, 255, 0.1) !important;
        transform: translateX(4px);
      }
      .custom-menu .ant-menu-item-selected {
        background: linear-gradient(90deg, #1890ff 0%, #096dd9 100%) !important;
        box-shadow: none !important;
        font-weight: 600;
      }
      .custom-menu .ant-menu-item-selected::after {
        display: none;
      }
      .custom-menu .ant-menu-item-icon {
        font-size: 18px;
        margin-right: 12px;
        display: flex;
        align-items: center;
      }
      .custom-menu.ant-menu-dark .ant-menu-item-selected {
        background: linear-gradient(90deg, #1890ff 0%, #096dd9 100%) !important;
        box-shadow: none !important;
      }
      .custom-menu.ant-menu-dark .ant-menu-item {
        color: rgba(255, 255, 255, 0.85);
      }
      .custom-menu.ant-menu-dark .ant-menu-item:hover {
        color: #fff;
      }
      .custom-menu.ant-menu-dark .ant-menu-item-selected {
        color: #fff;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  const handleMenuClick = ({ key }) => {
    const route = routes.find((r) => r.key === key);
    if (route && route.route) {
      navigate(route.route);
      setSelectedKey(key);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/authentication/sign-in");
      toast.success("با موفقیت خارج شدید!");
    } catch (error) {
      toast.error("خطا در خروج از حساب!");
    }
  };

  const toggleCollapsed = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    setMiniSidenav(dispatch, newCollapsed);
  };

  const menuItems = useMemo(() => {
    return routes
      .filter((route) => route.type === "collapse")
      .map((route) => ({
        key: route.key || route.route?.replace("/", "") || "",
        icon: getReactIcon(route.icon),
        label: route.name,
        title: route.name,
      }));
  }, [routes]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return (
    <Sider
      hidden={isMobile && location.pathname === '/Weight-Bridge'}
      collapsible
      collapsed={collapsed}
      onCollapse={toggleCollapsed}
      trigger={null}
      width={280}
      theme="dark"
      style={{
        overflow: "auto",
        height: "100vh",
        position: "fixed",
        right: 0,
        top: 0,
        bottom: 0,
        background: "linear-gradient(180deg, #001529 0%, #002140 100%)",
        boxShadow: "-2px 0 8px rgba(0, 0, 0, 0.15)",
        zIndex: 1000,
      }}
      className="custom-sider"
      breakpoint="lg"
      collapsedWidth={80}
    >
      {/* Logo Section */}
      <div
        style={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          padding: collapsed ? "0 16px" : "0 24px",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          background: "rgba(0, 0, 0, 0.2)",
        }}
      >
        {!collapsed && (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {brand && (
              <Avatar
                src={brand}
                size={40}
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "2px solid rgba(255, 255, 255, 0.2)",
                }}
              />
            )}
            <Text
              strong
              style={{
                color: "#fff",
                fontSize: 15,
                fontWeight: 600,
                fontFamily: "Vazir, sans-serif",
              }}
            >
              {"نرم افزار بارگو"}
            </Text>
          </div>
        )}
      
        <Tooltip title={collapsed ? "باز کردن منو" : "بستن منو"} placement="right">
          <Button
            type="text"
            icon={collapsed ? <MdMenuOpen style={{ fontSize: 18 }} /> : <MdMenu style={{ fontSize: 18 }} />}
            onClick={toggleCollapsed}
            style={{
              color: "#fff",
              fontSize: 16,
            }}
          />
        </Tooltip>
      </div>

      {/* Menu Section */}
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        onClick={handleMenuClick}
        items={menuItems}
        style={{
          background: "transparent",
          border: "none",
          padding: "16px 0",
          fontFamily: "Vazir, sans-serif",
        }}
        className="custom-menu"
      />

     
    </Sider>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
  brandName: "بارگو",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
